import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import { Event, ApiResponse, AISearchResponse, AISearchSuggestions, UnifiedSearchResponse, UnifiedSearchFilters } from '../types';

export interface EventFilters {
  page?: number;
  size?: number;
  sort?: string;
  filter?: string;
  search?: string;
}

export interface EventSummary {
  unacknowledged: number;
  active: number;
  resolved: number;
}

export type EventListResponse = ApiResponse<Event[]> & { summary?: EventSummary };

export interface CreateEventData {
  event_camera?: string;
  event_type?: string;
  event_tag?: string;
  event_note?: string;
  event_state?: string;
  event_location?: number;
  confidence?: number;
  event_metadata?: string;
}

export interface UpdateEventData extends Partial<CreateEventData> {
  acknowledged?: boolean;
  processed?: boolean;
}

export interface PersonSearchFilters {
  gender?: 'male' | 'female';
  clothing_color?: string;
  clothing_type?: string;
  limit?: number;
}

export interface VehicleSearchFilters {
  vehicle_type?: string;
  vehicle_color?: string;
  unit_number?: string;
  license_plate?: string;
  limit?: number;
}

class EventService {
  /**
   * Get all events with optional filters
   */
  async getEvents(filters?: EventFilters, signal?: AbortSignal): Promise<EventListResponse> {
    return apiClient.get<EventListResponse>(
      API_CONFIG.ENDPOINTS.EVENTS,
      filters,
      { signal }
    );
  }

  /**
   * Get event by ID
   */
  async getEventById(id: number): Promise<Event> {
    return apiClient.get<Event>(`${API_CONFIG.ENDPOINTS.EVENT}/${id}`);
  }

  /**
   * Get events by camera
   */
  async getEventsByCamera(cameraId: number): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `camera_id::${cameraId}`,
    });
  }

  /**
   * Get events by location
   */
  async getEventsByLocation(locationId: number): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `event_location::${locationId}`,
    });
  }

  /**
   * Get events by type
   */
  async getEventsByType(eventType: string): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `event_type::${eventType}`,
    });
  }

  /**
   * Get events by severity
   */
  async getEventsBySeverity(severity: string): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `event_severity::${severity}`,
    });
  }

  /**
   * Get events by state
   */
  async getEventsByState(state: string): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `event_state::${state}`,
    });
  }

  /**
   * Get unacknowledged events
   */
  async getUnacknowledgedEvents(): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `acknowledged::false`,
    });
  }

  /**
   * Get events by date range
   */
  async getEventsByDateRange(startDate: string, endDate: string): Promise<ApiResponse<Event[]>> {
    return this.getEvents({
      filter: `created_date_gte::${startDate},created_date_lte::${endDate}`,
    });
  }

  /**
   * Create new event
   */
  async createEvent(data: CreateEventData): Promise<Event> {
    return apiClient.post<Event>(API_CONFIG.ENDPOINTS.EVENT, data);
  }

  /**
   * Update event
   */
  async updateEvent(id: number, data: UpdateEventData): Promise<Event> {
    return apiClient.put<Event>(`${API_CONFIG.ENDPOINTS.EVENT}/${id}`, data);
  }

  /**
   * Delete event
   */
  async deleteEvent(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${API_CONFIG.ENDPOINTS.EVENT}/${id}`);
  }

  /**
   * Acknowledge event
   */
  async acknowledgeEvent(id: number): Promise<Event> {
    const { authService } = await import('./auth.service');
    const userId = authService.getUserId();
    
    return apiClient.put<Event>(`${API_CONFIG.ENDPOINTS.EVENT}/${id}`, {
      acknowledged: true,
      acknowledged_by: userId,
      acknowledged_at: new Date().toISOString(),
    });
  }

  /**
   * Mark event as processed
   */
  async processEvent(id: number): Promise<Event> {
    return apiClient.put<Event>(`${API_CONFIG.ENDPOINTS.EVENT}/${id}`, {
      processed: true,
    });
  }

  /**
   * Fetch and store event records from hardware
   */
  async fetchStoreEventRecords(
    cameraId: number,
    startDate: string,
    endDate: string
  ): Promise<{ message: string; count: number }> {
    return apiClient.post<{ message: string; count: number }>(
      '/camera-security/fetch-store-event-records',
      { camera_id: cameraId, start_date: startDate, end_date: endDate }
    );
  }

  // =====================
  // AI Search Methods
  // =====================

  /**
   * Natural language search for events
   */
  async searchEvents(query: string, limit = 20): Promise<AISearchResponse> {
    return apiClient.get<AISearchResponse>(
      '/camera-security/events/search',
      { q: query, limit }
    );
  }

  /**
   * Get search suggestions
   */
  async getSearchSuggestions(): Promise<AISearchSuggestions> {
    return apiClient.get<AISearchSuggestions>(
      '/camera-security/events/search/suggestions'
    );
  }

  /**
   * Search events by person attributes
   */
  async searchByPerson(filters: PersonSearchFilters): Promise<AISearchResponse> {
    const params: Record<string, string | number> = {};
    if (filters.gender) params.gender = filters.gender;
    if (filters.clothing_color) params.clothing_color = filters.clothing_color;
    if (filters.clothing_type) params.clothing_type = filters.clothing_type;
    if (filters.limit) params.limit = filters.limit;

    return apiClient.get<AISearchResponse>(
      '/camera-security/events/search/person',
      params
    );
  }

  /**
   * Search events by vehicle attributes
   */
  async searchByVehicle(filters: VehicleSearchFilters): Promise<AISearchResponse> {
    const params: Record<string, string | number> = {};
    if (filters.vehicle_type) params.vehicle_type = filters.vehicle_type;
    if (filters.vehicle_color) params.vehicle_color = filters.vehicle_color;
    if (filters.unit_number) params.unit_number = filters.unit_number;
    if (filters.license_plate) params.license_plate = filters.license_plate;
    if (filters.limit) params.limit = filters.limit;

    return apiClient.get<AISearchResponse>(
      '/camera-security/events/search/vehicle',
      params
    );
  }

  // =====================
  // Unified Search Methods
  // =====================

  /**
   * Unified search across events and visitor logs
   */
  async unifiedSearch(filters: UnifiedSearchFilters, signal?: AbortSignal): Promise<UnifiedSearchResponse> {
    const params: Record<string, string | number | boolean> = { q: filters.q };
    if (filters.location_id) params.location_id = filters.location_id;
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset !== undefined) params.offset = filters.offset;
    if (filters.include_events !== undefined) params.include_events = filters.include_events;
    if (filters.include_visitor_logs !== undefined) params.include_visitor_logs = filters.include_visitor_logs;

    return apiClient.get<UnifiedSearchResponse>(
      '/camera-security/search',
      params,
      { signal }
    );
  }
}

export const eventService = new EventService();
